// CSV export helper for the deliverables view. Walks the grouped Row[]
// (already chip-filtered, searched, and sorted, pre-pagination) and emits
// one CSV line per LEAF deliverable — one-offs, orphans, and the children
// of a recurring schedule. The synthetic recurring-parent row is excluded
// because it represents the schedule, not a deliverable.
//
// Status is the DERIVED status (so overdue / late surface), and RAG is the
// EFFECTIVE rag (so an overdue item reads Red even with no stored rating),
// matching what the table row shows. SOW / milestone columns resolve via
// caller-supplied id→display maps, falling back to the raw id.

import type { Row } from "./groupDeliverables";
import type { Deliverable, DeliverableDerivedStatus } from "../../../../types";
import { deriveDeliverableStatus } from "./deriveDeliverableStatus";
import { deriveEffectiveRag } from "./deriveEffectiveRag";
import { formatDayMonthYear } from "../../../../utils/formatShortDate";

const HEADER = [
  "Row type",
  "Name",
  "Type",
  "Owner",
  "Status",
  "RAG",
  "Due date",
  "Clause ref",
  "SOW",
  "Milestone",
  "Archived",
] as const;

// Human labels for the derived status, mirroring the deliverable status-pill
// vocabulary in TrackingStatusBadge so the export reads the same as the UI.
const STATUS_LABELS: Record<DeliverableDerivedStatus, string> = {
  "not-started": "Not started",
  submitted: "Submitted",
  "under-review": "Under review",
  "revision-requested": "Revision requested",
  "conditionally-accepted": "Conditionally accepted",
  "awaiting-resubmission": "Awaiting resubmission",
  accepted: "Accepted",
  rejected: "Rejected",
  closed: "Closed",
  overdue: "Overdue",
  late: "Late",
};

const TYPE_LABELS: Record<Deliverable["deliverableType"], string> = {
  deliverable: "Deliverable",
  obligation: "Obligation",
};

const OWNER_LABELS: Record<Deliverable["owner"], string> = {
  supplier: "Supplier",
  customer: "Customer",
  joint: "Joint",
};

const RAG_LABELS: Record<"green" | "amber" | "red", string> = {
  green: "Green",
  amber: "Amber",
  red: "Red",
};

interface BuildOptions {
  today: Date;
  /** id → display string (e.g. "SOW01 · Build"). Falls back to the raw id. */
  sowsById?: Record<string, string>;
  /** id → display name. Falls back to the raw id. */
  milestonesById?: Record<string, string>;
}

interface LeafRow {
  rowType: string;
  deliverable: Deliverable;
}

// Flatten the grouped rows to leaf deliverables, dropping the synthetic
// recurring-parent rows while keeping their children.
function toLeafRows(rows: Row[]): LeafRow[] {
  const leaves: LeafRow[] = [];
  for (const row of rows) {
    if (row.kind === "one-off") {
      leaves.push({ rowType: "One-off", deliverable: row.deliverable });
    } else if (row.kind === "orphan") {
      leaves.push({ rowType: "Orphan", deliverable: row.deliverable });
    } else if (row.kind === "child") {
      leaves.push({ rowType: "Occurrence", deliverable: row.deliverable });
    } else {
      // parent — emit its children only (the schedule row itself is synthetic)
      for (const child of row.children) {
        leaves.push({ rowType: "Occurrence", deliverable: child.deliverable });
      }
    }
  }
  return leaves;
}

function escapeCell(value: string): string {
  return `"${value.replace(/"/gu, '""')}"`;
}

export function buildDeliverablesCsv(rows: Row[], options: BuildOptions): string {
  const { today, sowsById, milestonesById } = options;
  const lines: string[] = [HEADER.map(escapeCell).join(",")];

  for (const { rowType, deliverable: d } of toLeafRows(rows)) {
    const derived = deriveDeliverableStatus(
      d.status,
      d.dueDate,
      today,
      d.submittedAt,
    );
    const effectiveRag = deriveEffectiveRag(d, today);
    const cells: string[] = [
      rowType,
      d.name,
      TYPE_LABELS[d.deliverableType],
      OWNER_LABELS[d.owner],
      STATUS_LABELS[derived],
      effectiveRag ? RAG_LABELS[effectiveRag] : "",
      d.dueDate ? formatDayMonthYear(d.dueDate) : "",
      d.clauseReference ?? "",
      d.sowId ? (sowsById?.[d.sowId] ?? d.sowId) : "",
      d.milestoneId ? (milestonesById?.[d.milestoneId] ?? d.milestoneId) : "",
      d.archivedAt ? "Yes" : "No",
    ];
    lines.push(cells.map(escapeCell).join(","));
  }

  return lines.join("\n");
}

function isoDateUTC(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function slugify(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, "-")
    .replace(/^-+|-+$/gu, "");
}

interface ContractRef {
  id: string;
  contractNumber?: string;
  title?: string;
}

export function deliverablesCsvFilename(
  contract: ContractRef,
  today: Date,
): string {
  // Prefer the contract number, then the title; sanitise to a slug and fall
  // back to the (already-safe) contract id when nothing sensible survives.
  const source = contract.contractNumber || contract.title || "";
  const slug = slugify(source) || contract.id;
  return `deliverables-${slug}-${isoDateUTC(today)}.csv`;
}
